import { MAX_POINTS } from '~/consts';
import { GAME_MODE, SingSetup } from '~/interfaces';
import { accuracyOf, NoteStats } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';
import { getAllStats, SongStats } from '~/modules/songs/stats/common';
import { normaliseProfileName } from '~/modules/user-profiles/profiles';

/** One play, as it concerns a single profile. */
export interface ProfilePlay {
  /** `SongStats` key - `artist-title`, see `getSongKey`. */
  songKey: string;
  score: number;
  /** How the notes went. Absent for plays recorded before this was measured. */
  notes?: NoteStats;
  /** Everyone else who sang that round, in the order recorded. */
  others: Array<{ name: string; score: number }>;
  /**
   * Everyone who sang that round, the profile itself included, in player order. Co-op records one
   * entry for the whole team under the roster joined with `, `, so this is where that name is split
   * back out - it is the only place the co-op roster survives. Neither form records which slot
   * anybody was on, but `scores` is written in player order (see the `songEnded` dispatch), so the
   * position in this list is the slot.
   */
  singers: string[];
  setup: SingSetup;
  date: string;
  /** How much of the song was sung, 0-1. Absent on plays recorded before it was tracked. */
  progress?: number;
}

export type Grade = (typeof GRADES)[number]['grade'];

/** How a profile did against the other singers in the rounds it shared with them. */
export interface HeadToHead {
  wins: number;
  losses: number;
  draws: number;
  /** Wins in a row counting back from the most recent contested round. */
  streak: number;
}

export interface ProfileStats {
  plays: ProfilePlay[];
  /** Distinct songs, which is not `plays.length` - the same song can be sung many times. */
  songCount: number;
  totalScore: number;
  averageScore: number;
  highestScore: number;
  /** Best score per song key, for "your record" style displays. */
  bestPerSong: Record<string, number>;
  /** The play behind each of `bestPerSong`, which is what that song's grade is read from. */
  bestPlayPerSong: Record<string, ProfilePlay>;
  /** The play behind `highestScore`; `null` with nothing sung. */
  bestPlay: ProfilePlay | null;
  lastPlayed: string | null;
  headToHead: HeadToHead;
  gradeDistribution: Record<Grade, number>;
  /**
   * Averaged over the plays whose notes were measured; `null` when none were - which includes a
   * profile that only sings co-op, since those rounds carry no notes at all (see `summariseNotes`).
   */
  averageAccuracy: number | null;
  bestCombo: number | null;
}

/**
 * Score as a share of the maximum a song can award. `MAX_POINTS` is the per-song ceiling the
 * engine scales to, so this is comparable across songs where the raw score is not.
 */
export const scoreToPercentage = (score: number) => Math.max(0, Math.min(1, score / MAX_POINTS));

export const GRADES = [
  { grade: 'S', min: 0.95 },
  { grade: 'A', min: 0.85 },
  { grade: 'B', min: 0.7 },
  { grade: 'C', min: 0.55 },
  { grade: 'D', min: 0.4 },
  { grade: 'E', min: 0 },
] as const;

const gradeForShare = (share: number) => GRADES.find(({ min }) => share >= min)!.grade;

export const scoreToGrade = (score: number) => gradeForShare(scoreToPercentage(score));

/**
 * The grade is how well the notes were sung, not how many points they earned: the score carries
 * bonuses - vibrato, combos, difficulty - and a grade read off it put a D beside 80% accuracy.
 * Only a play with no note counts, from before they were recorded or co-op, falls back on its score.
 */
export const gradeOf = ({ score, notes }: { score: number; notes?: NoteStats }): Grade =>
  notes && notes.total > 0 ? gradeForShare(accuracyOf(notes)) : scoreToGrade(score);

/** The best of several grades, or `null` for none. */
export const bestGrade = (grades: Grade[]): Grade | null =>
  GRADES.find(({ grade }) => grades.includes(grade))?.grade ?? null;

/**
 * Co-op reports one shared score for the whole team rather than one per singer, so a co-op round
 * is credited to every profile that took part, at the team score.
 */
const isCoOp = (setup: SingSetup) => setup.mode === GAME_MODE.CO_OP;

/** Every play in `stats` that the given name sang, in the order stored. */
export const playsForName = (name: string, stats: Record<string, SongStats>): ProfilePlay[] => {
  const normalised = normaliseProfileName(name);
  const plays: ProfilePlay[] = [];

  for (const [songKey, songStats] of Object.entries(stats)) {
    for (const record of songStats?.scores ?? []) {
      const named = record.scores.find((entry) => normaliseProfileName(entry.name) === normalised);
      // Co-op stores one entry for the whole team under the roster joined with `, `, so splitting
      // that name is both who sang the round and the only way to tell whether this profile was one
      // of them.
      const singers = isCoOp(record.setup)
        ? (record.scores[0]?.name ?? '')
            .split(',')
            .map((name) => name.trim())
            .filter(Boolean)
        : record.scores.map(({ name }) => name);
      // In co-op the team entry does not match this singer by name, so fall back to it - but only
      // when the roster actually names them. Without that test every co-op round in the store was
      // credited to every profile, so a profile that had never sung showed a page full of plays.
      const sang = singers.some((singer) => normaliseProfileName(singer) === normalised);
      const mine = named ?? (isCoOp(record.setup) && sang ? record.scores[0] : undefined);
      if (mine === undefined) continue;

      plays.push({
        songKey,
        score: mine.score,
        notes: named?.notes,
        // Measured against `mine` rather than `named`: in co-op that entry is this profile's own,
        // and otherwise the round listed the team, and so the singer, as who they sang *with*.
        others: record.scores.filter((entry) => entry !== mine).map(({ name, score }) => ({ name, score })),
        singers,
        setup: record.setup,
        date: record.date,
        progress: record.progress,
      });
    }
  }

  return plays;
};

/**
 * Rounds where somebody else sang for their own score, so there was something to win. Co-op is a
 * shared score by design, and a round sung alone has no opponent.
 */
const isContested = (play: ProfilePlay) => !isCoOp(play.setup) && play.others.length > 0;

const summariseHeadToHead = (plays: ProfilePlay[]): HeadToHead => {
  const contested = plays.filter(isContested).sort((a, b) => a.date.localeCompare(b.date));
  const result = { wins: 0, losses: 0, draws: 0, streak: 0 };

  for (const play of contested) {
    const best = Math.max(...play.others.map((other) => other.score));

    if (play.score > best) result.wins++;
    else if (play.score < best) result.losses++;
    else result.draws++;
  }

  // Counted back from the newest round, so it reads as "on a roll right now".
  for (let i = contested.length - 1; i >= 0; i--) {
    const best = Math.max(...contested[i].others.map((other) => other.score));
    if (contested[i].score <= best) break;
    result.streak++;
  }

  return result;
};

const emptyGradeDistribution = () => Object.fromEntries(GRADES.map(({ grade }) => [grade, 0])) as Record<Grade, number>;

/**
 * How two profiles did in the rounds they sang together. Read off one profile's plays, since each
 * play already carries every other singer's score.
 */
export const headToHeadBetween = (plays: ProfilePlay[], opponentName: string): HeadToHead & { rounds: number } => {
  const opponent = normaliseProfileName(opponentName);
  const shared = plays
    .filter((play) => !isCoOp(play.setup))
    .map((play) => ({
      play,
      theirs: play.others.find((other) => normaliseProfileName(other.name) === opponent),
    }))
    .filter(
      (entry): entry is { play: ProfilePlay; theirs: { name: string; score: number } } => entry.theirs !== undefined,
    )
    .sort((a, b) => a.play.date.localeCompare(b.play.date));

  const result = { wins: 0, losses: 0, draws: 0, streak: 0, rounds: shared.length };

  for (const { play, theirs } of shared) {
    if (play.score > theirs.score) result.wins++;
    else if (play.score < theirs.score) result.losses++;
    else result.draws++;
  }

  for (let i = shared.length - 1; i >= 0; i--) {
    if (shared[i].play.score <= shared[i].theirs.score) break;
    result.streak++;
  }

  return result;
};

export const summarisePlays = (plays: ProfilePlay[]): ProfileStats => {
  const bestPerSong: Record<string, number> = {};
  const bestPlayPerSong: Record<string, ProfilePlay> = {};
  let bestPlay: ProfilePlay | null = null;
  const gradeDistribution = emptyGradeDistribution();
  let totalScore = 0;
  let highestScore = 0;
  let lastPlayed: string | null = null;

  for (const play of plays) {
    totalScore += play.score;
    highestScore = Math.max(highestScore, play.score);
    if (bestPlay === null || play.score > bestPlay.score) bestPlay = play;
    const songBest = bestPlayPerSong[play.songKey];
    if (songBest === undefined || play.score > songBest.score) {
      bestPlayPerSong[play.songKey] = play;
      bestPerSong[play.songKey] = play.score;
    }
    gradeDistribution[gradeOf(play)]++;
    if (lastPlayed === null || play.date > lastPlayed) {
      lastPlayed = play.date;
    }
  }

  return {
    plays,
    songCount: Object.keys(bestPerSong).length,
    totalScore,
    averageScore: plays.length === 0 ? 0 : totalScore / plays.length,
    highestScore,
    bestPerSong,
    bestPlayPerSong,
    bestPlay,
    lastPlayed,
    headToHead: summariseHeadToHead(plays),
    gradeDistribution,
    ...summariseNotes(plays),
  };
};

/**
 * Plays recorded before the notes were measured are left out rather than counted as zero.
 *
 * That also takes every co-op round with it, permanently: co-op is recorded as one entry for the
 * whole team - one shared score, under the joined names - and there is nowhere on it to hang one
 * singer's notes, so `songEnded` does not send any. A profile that only ever sings co-op therefore
 * has no accuracy and no best combo at all, and none of the note achievements can move.
 *
 * Left as it is on purpose. The alternative is recording per-singer notes against a score that is
 * explicitly not per-singer, and the pair would then disagree about who did what. What is worth
 * fixing is that the screen shows the resulting blank without saying why - see `averageAccuracy`.
 */
const summariseNotes = (plays: ProfilePlay[]) => {
  const measured = plays.filter((play) => play.notes !== undefined);
  if (measured.length === 0) return { averageAccuracy: null, bestCombo: null };

  return {
    averageAccuracy: measured.reduce((total, play) => total + accuracyOf(play.notes!), 0) / measured.length,
    bestCombo: measured.reduce((best, play) => Math.max(best, play.notes!.maxCombo), 0),
  };
};

/**
 * Reads a profile's stats straight out of `SongStats`. Nothing about a profile's plays is stored
 * separately - the scores the game already records are the only source, so there is no second
 * store to keep in sync, prune, or outgrow localStorage with.
 */
export const fetchProfileStats = async (name: string): Promise<ProfileStats> =>
  summarisePlays(playsForName(name, await getAllStats()));

/** One pass over `SongStats` for several profiles, for list screens. */
export const fetchStatsForNames = async (names: string[]): Promise<Record<string, ProfileStats>> => {
  const stats = await getAllStats();

  return Object.fromEntries(names.map((name) => [name, summarisePlays(playsForName(name, stats))]));
};
